import React from "react";
import { Link } from "react-router-dom";
import { Globe, MapPin, GraduationCap } from "lucide-react";
import { CLINIC } from "@/lib/clinicInfo";

/**
 * AuthorBio — E-E-A-T signal card shown at the bottom of every blog article.
 * Content is verbatim per the clinic's editorial guidance.
 */
export default function AuthorBio() {
  return (
    <aside
      className="mt-16 rounded-2xl border border-border bg-card overflow-hidden"
      data-testid="author-bio"
    >
      <div className="p-6 sm:p-8 grid sm:grid-cols-[auto,1fr] gap-6">
        <div className="flex sm:block justify-center">
          <img
            src={CLINIC.doctorPhoto}
            alt="Dr. Gurkiran Kaur, Founder & Director, Nehchal Eye Centre Noida"
            className="h-28 w-28 sm:h-36 sm:w-36 rounded-full object-cover object-top ring-2 ring-primary/15"
            loading="lazy"
          />
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-2">
            About the Author
          </div>
          <h2 className="font-serif text-2xl text-foreground mb-1">
            Dr. Gurkiran Kaur, MS (Ophthalmology), MBBS
          </h2>
          <p className="text-sm text-primary mb-4">
            Founder &amp; Director, Nehchal Eye Centre, Noida
          </p>

          <p className="text-sm sm:text-base text-foreground/80 leading-7">
            Dr. Gurkiran Kaur is a comprehensive ophthalmologist and oculoplasty
            specialist with advanced training in cataract surgery, eyelid and
            lacrimal disorders, facial aesthetics, and comprehensive eye care.
            She completed her MBBS from the{" "}
            <strong className="text-foreground">
              Armed Forces Medical College (AFMC), Pune
            </strong>
            , followed by an MS in Ophthalmology from{" "}
            <strong className="text-foreground">
              King George&apos;s Medical University (KGMU), Lucknow
            </strong>
            . She further trained at the prestigious{" "}
            <strong className="text-foreground">
              L V Prasad Eye Institute (LVPEI), Hyderabad
            </strong>
            , and received advanced aesthetic surgical training under
            internationally renowned facial plastic surgeon{" "}
            <strong className="text-foreground">Dr. Ramtin Kassir</strong> in
            New York.
          </p>

          <p className="text-sm sm:text-base text-foreground/80 leading-7 mt-4">
            As the Founder and Director of Nehchal Eye Centre, Noida, Dr. Kaur
            is committed to delivering evidence-based, ethical, and
            patient-centred eye care using modern technology with a
            compassionate approach. Her areas of expertise include cataract
            surgery, comprehensive ophthalmology, oculoplasty, eyelid surgery,
            dry eye management, and periocular aesthetic procedures.
          </p>

          <p className="text-sm sm:text-base text-foreground/80 leading-7 mt-4">
            Through this blog, she shares practical, medically accurate
            information to help patients better understand eye conditions,
            treatment options, and ways to protect their vision.
          </p>
        </div>
      </div>

      <div className="border-t border-border bg-secondary/40 px-6 sm:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-foreground/75">
          <span className="font-serif text-base text-primary">Nehchal Eye Centre</span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-primary" /> Noida, Uttar Pradesh
          </span>
          <a
            href="https://www.nehchaleye.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-primary"
            data-testid="author-bio-site-link"
          >
            <Globe className="h-3.5 w-3.5 text-primary" /> www.nehchaleye.com
          </a>
        </div>
        <Link
          to="/about"
          className="inline-flex items-center gap-1.5 text-primary text-sm hover:opacity-80"
          data-testid="author-bio-about-link"
        >
          <GraduationCap className="h-3.5 w-3.5" /> Full credentials
        </Link>
      </div>
    </aside>
  );
}
